<template>
    <pendo-modal
        :visible="show"
        :min-width="572"
        width="70%"
        max-width="800"
        :min-height="540"
        height="auto"
        class="welcome-mat__modal"
        @close="dismissModal">
        <template #body>
            <div class="welcome-mat">
                <div class="welcome-mat__content">
                    <span class="welcome-mat__text-preamble">Welcome, {{ user.first }}!</span>
                    <span class="welcome-mat__text-header">Let’s learn how Pendo can help you understand and guide your users</span>
                    <span class="welcome-mat__text-caption">To get you started, we’ve loaded sample data in your subscription. As soon as you install,
                        Pendo will start collecting live data from your product. Until then, let’s show you what we’re
                        all about.<br><br>What do you want to see first?</span>
                    <div
                        data-welcome-mat-guide="1"
                        class="welcome-mat__guide"
                        :class="{ 'welcome-mat__guide--completed': isGuideCompleted(1) }"
                        @click="hideModal">
                        <pendo-icon
                            :type="iconForGuide(1)"
                            size="22"
                            class="welcome-mat__guide-icon" />
                        <span>Create Dashboards to monitor your product</span>
                        <pendo-icon
                            type="chevron-right"
                            size="16"
                            class="welcome-mat__guide-chevron" />
                    </div>
                    <div
                        data-welcome-mat-guide="2"
                        class="welcome-mat__guide"
                        :class="{ 'welcome-mat__guide--completed': isGuideCompleted(2) }"
                        @click="hideModal">
                        <pendo-icon
                            :type="iconForGuide(2)"
                            size="22"
                            class="welcome-mat__guide-icon" />
                        <span>Explore Behavior Analytics to understand users</span>
                        <pendo-icon
                            type="chevron-right"
                            size="16"
                            class="welcome-mat__guide-chevron" />
                    </div>
                    <div
                        data-welcome-mat-guide="3"
                        class="welcome-mat__guide"
                        :class="{ 'welcome-mat__guide--completed': isGuideCompleted(3) }"
                        @click="hideModal">
                        <pendo-icon
                            :type="iconForGuide(3)"
                            size="22"
                            class="welcome-mat__guide-icon" />
                        <span>Design Guides and publish in-product messaging</span>
                        <pendo-icon
                            type="chevron-right"
                            size="16"
                            class="welcome-mat__guide-chevron" />
                    </div>
                </div>
                <div class="welcome-mat__side">
                    <img
                        src="@img/welcome-mat.png"
                        class="welcome-mat__side-image">
                    <span class="welcome-mat__side-caption">
                        Discover, learn, test and take action.
                    </span>
                </div>
            </div>
        </template>
        <template #footer />
    </pendo-modal>
</template>

<script>
import { PendoModal, PendoIcon } from '@pendo/components';
import { mapGetters, mapState } from 'vuex';
import { EventBus } from '@utils/event-bus.js';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import { FREE_CONSTANTS } from '@/constants';
import { toggleSampleDataTooltip } from '@/free/sample-data-banner/sample-data-banner.vue';
// eslint-disable-next-line no-redeclare
/* global pendo */

// As of APP-78957, the welcome mat will only appear via guides.
// It used to automatically redirect new users to demo mode, but that was messing with roadmaps redirect
export function requestWelcomeMat () {
    window.localStorage.setItem(FREE_CONSTANTS.SampleDataLocalStorageKey, true);
    window.localStorage.setItem(FREE_CONSTANTS.WelcomeMatLocalStorageKey, true);
    window.localStorage.setItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey, 'true');
    PerSubscriptionUserStorage.set(FREE_CONSTANTS.WelcomeMatSeenDatastoreKey, true);
}

export function openWelcomeMat () {
    EventBus.$emit('openWelcomeMat');
}

export default {
    name: 'WelcomeMat',
    components: {
        PendoModal,
        PendoIcon,
    },
    data () {
        return {
            visible: false,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            // eslint-disable-next-line internal-rules/no-unused-properties
            subscription: (state) => state.subscription.active,
        }),
        ...mapGetters({
            getFeatureFlag: 'subscription/getFeatureFlag',
            preSampleDataSubId: 'auth/preSampleDataSubId',
            // eslint-disable-next-line internal-rules/no-unused-properties
            isImpersonating: 'auth/isImpersonating',
            subscriptions: 'subscription/list',
            // eslint-disable-next-line internal-rules/no-unused-properties
            isPendoFree: 'subscription/isPendoFree',
        }),
        onSampleData () {
            return this.getFeatureFlag('sampleDataSubscription') && this.preSampleDataSubId !== null;
        },
        show () {
            return this.onSampleData && this.visible;
        },
    },
    created () {
        EventBus.$on('openWelcomeMat', this.showModal);
        window.pendoWelcomeMat = {
            show: this.showModal,
            goToSetup: this.goToSetup,
        };
    },
    mounted () {
        this.requestedInApp();
    },
    beforeUnmount () {
        EventBus.$off('openWelcomeMat', this.showModal);
        delete window.pendoWelcomeMat;
    },
    methods: {
        ...{ requestWelcomeMat },
        requestedInApp () {
            if (window.localStorage.getItem(FREE_CONSTANTS.WelcomeMatLocalStorageKey)) {
                this.showModal();
                window.localStorage.removeItem(FREE_CONSTANTS.WelcomeMatLocalStorageKey);

                return true;
            }

            return false;
        },
        showModal () {
            toggleSampleDataTooltip(false);
            this.visible = true;
        },
        hideModal () {
            this.visible = false;
        },
        dismissModal () {
            toggleSampleDataTooltip(true);
            this.hideModal();
        },
        isGuideCompleted (index) {
            const guideId = FREE_CONSTANTS[`WelcomeMatGuideName${index}`];
            const guide = pendo.findGuideByName(guideId);

            return guide !== null && guide.isComplete();
        },
        iconForGuide (index) {
            const icons = ['check-circle-2', 'chart-line', 'feature', 'guide'];

            return this.isGuideCompleted(index) ? icons[0] : icons[index];
        },
        goToSetup () {
            const sub = this.subscriptions.find((x) => x.id === this.preSampleDataSubId);
            window.location.assign(`/setup?setupKey=${sub.setupKey}`);
        },
    },
};
</script>
<style scoped lang="less">
.welcome-mat__modal {
    :deep(.pendo-modal__container) {
        display: flex;
        flex-direction: column;

        .pendo-modal__header,
        .pendo-modal__footer {
            border: 0;
            height: 0;
            min-height: 0;
            padding: 0;
        }

        .pendo-modal__body {
            display: flex;
            flex-grow: 1;
            padding: 0;
        }

        .pendo-modal__close-button {
            position: absolute;
            top: 16px;
            right: 16px;

            .pendo-icon__x {
                width: 24px;

                &:hover {
                    color: var(--palette-gray-0);
                }
            }
        }
    }
}

.welcome-mat {
    display: flex;
    flex-direction: row;
    width: 100%;
}

.welcome-mat__content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex: 2;
    height: 100%;
    padding: 7% 40px;
}

.welcome-mat__text-preamble {
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--palette-gray-70);
    margin-bottom: 16px;
}

.welcome-mat__text-header {
    font-style: normal;
    font-weight: bold;
    font-size: 22px;
    line-height: 26px;
    color: var(--palette-gray-100);
    margin-bottom: 16px;
}

.welcome-mat__text-caption {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 17px;
    color: var(--palette-gray-100);
    margin-bottom: 16px;
}

.welcome-mat__guide {
    display: flex;
    flex-direction: row;
    align-items: center;
    border: 1px solid var(--palette-gray-40);
    border-radius: 4px;
    padding: 20px 16px;
    margin: 8px 0;
    cursor: pointer;

    &,
    & *,
    &:hover,
    &:hover * {
        transition: 0.1s;
    }

    &:hover {
        border: 2px solid var(--palette-purple-90);
        box-shadow: 0px 2px 6px var(--semantic-drop-shadow-base);
        margin: 7px -1px;

        * {
            color: var(--palette-purple-90) !important;
        }
    }

    span {
        font-weight: 600;
        font-style: normal;
        font-size: 14px;
        line-height: 18px;
        flex: 1;
        margin: 0 16px;
        color: var(--palette-gray-100);
    }

    .welcome-mat__guide-icon {
        color: var(--palette-gray-100);
    }

    .welcome-mat__guide-chevron {
        color: var(--palette-gray-60);
    }
}

.welcome-mat__guide--completed {
    .welcome-mat__guide-icon {
        color: var(--palette-green-70);
        background-color: rgba(var(--raw-palette-green-40), 0.17);
        padding: 4px;
        margin: -4px;
        border-radius: 50%;
    }

    &:hover {
        .welcome-mat__guide-icon {
            color: var(--palette-purple-90);
            background-color: rgba(var(--raw-palette-purple-30), 0.17);
        }
    }
}

.welcome-mat__side {
    height: 100%;
    flex: 1;
    padding: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--palette-gray-100);
    color: var(--palette-gray-0);
}

.welcome-mat__side-image {
    width: 110%;
    margin-bottom: 16px;
}

.welcome-mat__side-caption {
    font-style: normal;
    font-weight: bold;
    font-size: 22px;
    line-height: 26px;
}
</style>
